import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Mail } from 'lucide-react'
import { Route as rootRoute } from '../__root'

/**
 * Verify-email-pending route — shown immediately after successful sign-up.
 *
 * Tells the user that a verification link has been dispatched to their inbox
 * and they must click it to activate their account. This page is purely
 * informational; the verification handshake itself lives at
 * `/auth/verify-email?userId=…&token=…`.
 *
 * Visual style intentionally mirrors `sign-up.tsx` / `sign-in.tsx`: the same
 * brand-tinted gradient, `font-display` heading, the recurring 1×12 accent
 * bar, and a single soft-ringed glyph as the focal point. Everything uses
 * direction-neutral utilities (`px-/py-/mb-/mt-/space-y-*`) because the host
 * `<html>` is `dir="rtl"`.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/verify-email-pending',
  component: VerifyEmailPendingPage,
})

function VerifyEmailPendingPage() {
  const { t } = useTranslation('auth')

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-bl from-brand-50/60 via-white to-brand-100/30 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-brand-100 p-8 sm:p-10 text-center">
          <div
            className="inline-block h-1 w-12 rounded-full bg-brand-500 mb-4"
            aria-hidden="true"
          />

          <div
            className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60"
            aria-hidden="true"
          >
            <Mail className="h-10 w-10 text-brand-600" strokeWidth={1.5} />
          </div>

          <h1 className="text-2xl font-bold font-display text-brand-900 tracking-tight mb-3">
            {t('verifyEmail')}
          </h1>
          <p className="text-brand-800 leading-relaxed mb-6">
            {t('verifyEmailMessage')}
          </p>

          <a
            href="/auth/sign-in"
            className="inline-block font-semibold text-brand-600 hover:text-brand-700 hover:underline"
          >
            {t('signIn')}
          </a>
        </div>
      </div>
    </main>
  )
}
